import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import { ColorPickerDirective } from 'ngx-color-picker';
import { colorHexStripAlpha } from '../utils/color-converter';
import { colorPickerTranslations } from './color-picker.translations';

export type ColorPickerWidth = 'small' | 'medium' | 'default';

export type ColorPickerPosition =
  | 'auto'
  | 'top-left'
  | 'top-right'
  | 'bottom-left'
  | 'bottom-right'
  | 'bottom';

export type ColorPickerArrowPosition = 'top-right' | 'default';

/**
 * ColorPickerComponent - Reusable color picker component.
 *
 * ## Usage:
 * With reactive forms:
 * ```html
 * <sd-color-picker [formControl]="form.controls.colorControl"></sd-color-picker>
 * ```
 * Without reactive forms:
 * ```html
 * <sd-color-picker [color]="colorRGB" (colorChanged)="setColor($event)"></sd-color-picker>
 * ```
 */
@Component({
  selector: 'sd-color-picker',
  imports: [CommonModule, ColorPickerDirective],
  styles: `
    :host {
      display: inline-block;
      position: relative;
      width: 100%;
      height: 100%;
    }
  `,
  template: `
    <input
      [colorPicker]="colorValue()"
      [cpPosition]="dialogPosition()"
      [cpPositionOffset]="dialogOffset()"
      [cpDisabled]="disabledState()"
      [disabled]="disabledState()"
      [cpWidth]="width()"
      [cpOutputFormat]="'hex'"
      [cpAlphaChannel]="'disabled'"
      [cpCancelButton]="true"
      [cpCancelButtonText]="translations.colorPicker.cancel.text"
      [cpCancelButtonClass]="'modus-button modus-button-medium modus-basic-button px-2'"
      [cpOKButton]="true"
      [cpOKButtonText]="translations.colorPicker.ok.text"
      [cpOKButtonClass]="'modus-button modus-button-medium modus-flat-button-primary px-2'"
      [tabindex]="tabindex()"
      [ngClass]="{
        'cursor-pointer': !disabledState(),
        'arrow-top-right': dialogArrowPosition() === 'top-right',
      }"
      (cpToggleChange)="toggleChanged.emit($event)"
      (colorPickerChange)="setColor($event)"
      style="background-color: {{ colorValue() }};"
      class="w-full h-full border border-gray-0 rounded-2xl indent-[-10rem]"
      readonly
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ColorPickerComponent {
  readonly translations = colorPickerTranslations();

  readonly color = input<string>();
  readonly dialogOffset = input('0px');
  readonly dialogPosition = input<ColorPickerPosition>('auto');
  readonly dialogArrowPosition = input<ColorPickerArrowPosition>('default');
  readonly dialogSize = input<ColorPickerWidth>('default');
  readonly disabled = input<boolean>(false);
  readonly tabindex = input<string | number | null>(null);

  // https://angular.dev/guide/components/outputs#choosing-event-names
  readonly colorChanged = output<string>();
  readonly toggleChanged = output<boolean>();

  readonly width = computed(() => {
    switch (this.dialogSize()) {
      case 'small':
        return '155px';
      case 'medium':
        return '180px';
      default:
        return '230px';
    }
  });

  private readonly internalColorValue = signal<string>('');
  readonly colorValue = computed(() => {
    const color = this.internalColorValue() ?? '';
    return colorHexStripAlpha(color) ?? '';
  });

  private readonly internalDisabledState = signal<boolean | null>(null);
  readonly disabledState = computed(() => this.internalDisabledState() ?? this.disabled());

  constructor() {
    effect(() => {
      const inputColor = this.color();
      if (inputColor !== undefined) {
        this.setInternalColorValue(inputColor);
      }
    });
  }

  setColor(color: string): void {
    const rgba = color.replace('#', '');

    const valueChanged =
      rgba.localeCompare(this.internalColorValue(), 'en', { sensitivity: 'base' }) !== 0;
    if (!valueChanged) return;

    this.internalColorValue.set(rgba);
    this.colorChanged.emit(rgba);
  }

  setInternalColorValue(color: string) {
    queueMicrotask(() => {
      this.internalColorValue.set(color);
    });
  }

  setDisabledState(isDisabled: boolean) {
    queueMicrotask(() => {
      this.internalDisabledState.set(isDisabled);
    });
  }
}
